import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import moment from 'moment';
import { Case, CaseType } from 'src/app/models/case';
import { NiceClass } from 'src/app/models/niceClass';
import { PropertyOffice } from 'src/app/models/propertyOffice';
import { Trademark } from 'src/app/models/trademark';
import { PropertyOfficeService } from 'src/app/services/property-office.service';
import { TrademarkService } from 'src/app/services/trademark.service';
import { NiceSelectComponent } from '../nice-select/nice-select.component';
import { SelectComponent } from '../select/select.component';
import { ProductService } from 'src/app/models/productService';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-case-content',
  templateUrl: './case-content.component.html',
  styleUrls: ['./case-content.component.scss']
})
export class CaseContentComponent implements OnInit, OnChanges {
  @Input() mode: "CONSULTATION" | "EDITION" | "COMPARISON" = "CONSULTATION";
  @Input() case?: Case;
  @Input() propertyOffice?: PropertyOffice;
  @Input() productsServices?: ProductService[];
  @Input() trademarkProductsServices?: ProductService[];

  @ViewChild("caseTypeSelect") caseTypeSelect?: SelectComponent;
  @ViewChild("niceSelect") niceSelect?: NiceSelectComponent;


  trademark?: Trademark;
  niceClassification: number[] = [];
  period: {
    notificationDate?: moment.Moment;
    start?: moment.Moment;
    end?: moment.Moment;
  } = {
      notificationDate: undefined,
      start: undefined,
      end: undefined
    };
  type?: CaseType;
  lang?: string;



  caseTypes: {
    value: string,
    label: string,
    selected: boolean,
    disabled: boolean
  }[] = [
      {
        value: CaseType.PRINCIPAL,
        label: "CASE.TYPE_PRINCIPAL",
        selected: false,
        disabled: false
      },
      {
        value: CaseType.COUNTERCLAIM,
        label: "CASE.TYPE_COUNTERCLAIM",
        selected: false,
        disabled: false
      },
      {
        value: CaseType.RENEWAL_MAINTENANCE,
        label: "CASE.TYPE_RENEWAL_MAINTENANCE",
        selected: false,
        disabled: true
      }
    ];


  langs: {
    value: string,
    label: string,
    selected: boolean
  }[] = [
      {
        value: "cn",
        label: "LANGUAGES.cn",
        selected: false
      },
      {
        value: "en",
        label: "LANGUAGES.en",
        selected: false
      },
      {
        value: "fr",
        label: "LANGUAGES.fr",
        selected: false
      },
      {
        value: "de",
        label: "LANGUAGES.de",
        selected: false
      },
      {
        value: "it",
        label: "LANGUAGES.it",
        selected: false
      },
      {
        value: "es",
        label: "LANGUAGES.es",
        selected: false
      }
    ]

  constructor(
    private propertyOfficeService: PropertyOfficeService,
    private trademarkService: TrademarkService,
    private translate: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.populateEntities();


  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes &&
      (changes['case'] && changes['case']['previousValue'] != changes['case']['currentValue'])
      || (changes['mode'] && changes['mode']['previousValue'] != changes['mode']['currentValue'])) {
      this.populateEntities();
    }
  }

  async populateEntities() {
    if (this.case) {
      if (this.case.trademark?.ref) {
        try {
          const trademark = await this.trademarkService.retrieve(this.case.trademark.ref);
          if (trademark) {
            this.trademark = {
              _id: trademark._id!,
              name: trademark.name!,
              identifierNumber: trademark.identifierNumber,
              countryOfDesignation: trademark.countryOfDesignation,
              niceClassification: trademark.niceClassification?.map((niceClass) => ({ classNumber: niceClass.classNumber })),
              reproductionURI: trademark.reproductionURI
            }
          }
          this.caseTypes.find(ct => ct.value === CaseType.RENEWAL_MAINTENANCE)!.disabled = this.trademark?.countryOfDesignation !== "US";
        } catch (err) {
          console.error("Error occured when retrieving case trademark : ", err);
        }

      }
      if (this.case.niceClassification) {
        this.niceClassification = this.case.niceClassification;
      }
      if (this.case.timePeriod) {
        this.period.start = moment(this.case.timePeriod.start);
        this.period.end = moment(this.case.timePeriod.end);
      }
      if (this.case.lang) {
        this.lang = this.case.lang;
      }
      if (this.case.type) {
        this.type = this.case.type;
      }
    }
  }

  onTrademarkChange(event: { value?: Trademark }) {
    if (event?.value) {
      this.trademark = {
        _id: event.value._id!,
        name: event.value.name!,
        identifierNumber: event.value.identifierNumber,
        countryOfDesignation: event.value.countryOfDesignation,
        niceClassification: event.value.niceClassification?.map(niceClass => ({ classNumber: niceClass.classNumber })) ?? []
      };
    } else {
      this.trademark = {
        _id: '',
        name: '',
        countryOfDesignation: '',
        identifierNumber: ''
      };
    }
    this.niceClassification = [];
    if (this.case && this.trademark) {
      this.case.trademark = {
        ref: this.trademark._id!,
        name: this.trademark.name!,
        identifierNumber: this.trademark.identifierNumber,
        countryOfDesignation: this.trademark.countryOfDesignation
      };
    }

    if (this.trademark?.countryOfDesignation !== "US")
      this.caseTypeSelect?.select(this.caseTypes.find(ct => ct.value === CaseType.PRINCIPAL));
    this.caseTypes.find(ct => ct.value === CaseType.RENEWAL_MAINTENANCE)!.disabled = this.trademark?.countryOfDesignation !== "US";
  }

  onProductServiceChange(event: any) {
    if (event?.value) {
      this.productsServices = event.value;
    }
    if (this.case && this.productsServices) {
      this.case.productsServices = this.productsServices.map((ps) => ({
        ref: ps._id!,
        label: ps.label,
        classNumber: ps.classNumber,
        trademark: ps.trademark!.ref
      }));
    }
  }

  onNiceClassificationChange(event: any) {
    this.niceClassification = event.value.map((niceClass: NiceClass) => niceClass.classNumber);
    if (this.case) {
      this.case.niceClassification = this.niceClassification;
    }
  }

  onPeriodChange(event: any) {
    this.period = {
      ...this.period,
      start: event.start,
      end: event.end
    };
    if (this.case && this.period.start && this.period.end) {
      this.case.timePeriod = {
        ...this.case.timePeriod,
        start: this.period.start,
        end: this.period.end
      }
    }
    console.log(this.case?.timePeriod, this.period)
  }

  onPropertyOfficeChange(event: any) {
    if (event?.value) {
      this.propertyOffice = event.value;
      if (this.case) {
        this.case.propertyOffice = this.propertyOffice?._id;
      }

    }
  }

  onCaseTypeChange(event: any) {
    if (event?.value) {
      this.type = event.value.value;
      if (this.case && this.type) {
        this.case.type = this.type;
      }
    }
  }

  onLangChange(event: any) {
    if (event?.value) {
      this.lang = event.value.value;
      if (this.case && this.lang) {
        this.case.lang = this.lang
      }
    }
  }

  validate(field: string) {
    if (field === "trademark" && (!this.trademark || !this.trademark?._id)) {
      return {
        value: true,
        message: this.translate.instant('CASE.TRADEMARK_REQUIRED')
      }
    } else if (field === "niceClassification" && (!this.niceClassification || this.niceClassification.length === 0)) {
      return {
        value: true,
        message: this.translate.instant('CASE.NICE_CLASSIFICATION_REQUIRED')
      }
    } else if (field === "propertyOffice" && (!this.propertyOffice || !this.propertyOffice._id)) {
      return {
        value: true,
        message: this.translate.instant('CASE.PROPERTY_OFFICE_REQUIRED')
      }
    } else if (field === "type" && !this.type) {
      return {
        value: true,
        message: this.translate.instant('CASE.TYPE_REQUIRED')
      }
    } else if (field === "period" && (!this.period || !this.period.start || !this.period.end)) {
      return {
        value: true,
        message: this.translate.instant('CASE.PERIOD_REQUIRED')
      }
    } else if (field === "lang" && !this.lang) {
      return {
        value: true,
        message: this.translate.instant('CASE.LANG_REQUIRED')
      }
    } else {
      return {
        value: false,
        message: undefined
      }
    }
  }

  formatYear(date: Date | string) {
    return moment(date).format("YYYY");
  }

  formatDate(date: moment.Moment) {
    return moment(date).format("ll");
  }

  classesRestrictions() {
    return (this.trademark && this.trademark.niceClassification) ? this.trademark.niceClassification.map((n) => n.classNumber) : [];
  }

  getProductsServices(classNumber: number) {
    if (this.hasProductsServices()) {
      return (this.case && this.case.productsServices) ? this.case.productsServices.filter((ps) => ps.classNumber === classNumber).map((ps) => ps.label) : []
    } else if (this.trademarkProductsServices) {
      return this.trademarkProductsServices.filter((ps) => ps.classNumber === classNumber).map((ps) => ps.label)
    } else {
      return [];
    }
  }

  hasProductsServices() {
    return (this.case && this.case.productsServices) ? this.case.productsServices.length > 0 : false;
  }
}
